"use client";

import { useState } from "react";
import { usePathname } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { toast } from "sonner";

import { ResponsiveModal } from "@/components/ui/responsive-modal";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { acceptTermsAction } from "@/app/actions/terms";
import { handleExternalLinkClick } from "@/lib/utils/external-link";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Layout-level EULA consent gate for any signed-in user whose
 * users.terms_accepted_at is NULL — covers both existing users (whose
 * accounts pre-date the EULA flow) and brand-new social OAuth signups.
 *
 * iOS App Store Guideline 1.2 — every active account must have evidence of
 * an active EULA consent action.
 *
 * Non-dismissible by design: there is no close button, and onOpenChange is a
 * no-op so overlay clicks / ESC / Vaul swipe-down cannot bypass the gate.
 */
export function TermsAcceptGate() {
  const t = useTranslations("common.auth.terms");
  const locale = useLocale();
  const pathname = usePathname() ?? "";
  // 로그인 상태는 AuthProvider 에서 — 서버 확인이 끝난 로그인 사용자만 대상.
  // (loading 동안은 게이트를 띄우지 않는다: 캐시된 HTML 에 사용자별 상태가 없으므로)
  const { status, user, refresh } = useAuth();
  const needsConsent =
    status === "authenticated" && !!user && !user.terms_accepted_at;
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!needsConsent) return null;
  // Skip on routes the user must access in order to actually give consent:
  //  - /auth/*           : OAuth callback / error pages
  //  - /help/terms       : the terms text itself (linked from the gate)
  //  - /help/privacy     : the privacy policy text (linked from the gate)
  // Without this, the gate creates an unbreakable loop where the user can't
  // open the documents they're being asked to agree to.
  if (
    pathname.includes("/auth/") ||
    pathname.includes("/help/terms") ||
    pathname.includes("/help/privacy")
  ) {
    return null;
  }

  const canSubmit = agreeTerms && agreePrivacy && !submitting;

  const handleAccept = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const result = await acceptTermsAction();
      if ("error" in result) {
        toast.error(t("acceptError"));
        return;
      }
      // 프로필을 다시 받아 terms_accepted_at 이 채워지면 게이트가 사라진다.
      // (layout 은 더 이상 사용자를 모르므로 router.refresh() 로는 갱신되지 않는다)
      await refresh();
    } catch (error) {
      console.error("[TermsAcceptGate] accept failed", error);
      toast.error(t("acceptError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ResponsiveModal
      open
      // Block every dismissal attempt — Apple Guideline 1.2 requires an
      // explicit consent action with no escape hatch.
      onOpenChange={() => {}}
      showCloseButton={false}
      title={t("modalTitle")}
      // Description omitted on purpose — the two checkbox labels already
      // tell the user what they're agreeing to, and the modal feels tight
      // without an extra paragraph above them.
      drawerHeight="fit"
      footer={
        <Button
          type="button"
          onClick={handleAccept}
          disabled={!canSubmit}
          className="h-12 w-full rounded-full bg-orange-500 text-base font-semibold text-white hover:bg-orange-600 disabled:bg-gray-300"
        >
          {submitting ? t("submitting") : t("acceptButton")}
        </Button>
      }
    >
      <div className="space-y-4 py-2 text-sm">
        <div className="flex items-start gap-3">
          <Checkbox
            id="gate-agree-terms"
            checked={agreeTerms}
            onCheckedChange={(v) => setAgreeTerms(v === true)}
            disabled={submitting}
            className="h-5 w-5 border-gray-400 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
          />
          <Label
            htmlFor="gate-agree-terms"
            className="flex-1 text-gray-700 font-normal cursor-pointer leading-snug"
          >
            {t("agreeTerms")}{" "}
            <a
              href={`/${locale}/help/terms`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange-500 underline-offset-2 hover:underline"
              onClick={handleExternalLinkClick(`/${locale}/help/terms`)}
            >
              {t("view")}
            </a>
          </Label>
        </div>
        <div className="flex items-start gap-3">
          <Checkbox
            id="gate-agree-privacy"
            checked={agreePrivacy}
            onCheckedChange={(v) => setAgreePrivacy(v === true)}
            disabled={submitting}
            className="h-5 w-5 border-gray-400 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
          />
          <Label
            htmlFor="gate-agree-privacy"
            className="flex-1 text-gray-700 font-normal cursor-pointer leading-snug"
          >
            {t("agreePrivacy")}{" "}
            <a
              href={`/${locale}/help/privacy`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange-500 underline-offset-2 hover:underline"
              onClick={handleExternalLinkClick(`/${locale}/help/privacy`)}
            >
              {t("view")}
            </a>
          </Label>
        </div>
        <p className="pt-2 text-center text-xs text-gray-400">
          {t("noToleranceHint")}
        </p>
      </div>
    </ResponsiveModal>
  );
}
